import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { BannerEvent } from '../../../api/bannerOffers/types';
import { daysUntil, eventDurationDays, formatDateRange, getEventStatus, getOverlappingIndexes } from '../utils';

type Props = {
  events: BannerEvent[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDuplicate: (index: number) => void;
  onDelete: (index: number) => void;
};

const statusChip = (event: BannerEvent) => {
  const status = getEventStatus(event);
  if (status === 'live') {
    return <Chip size='small' color='success' label='Live now' />;
  }
  if (status === 'upcoming') {
    const days = daysUntil(event.start);
    return <Chip size='small' variant='outlined' color='primary' label={days === 1 ? 'Tomorrow' : `In ${days} days`} />;
  }
  return <Chip size='small' variant='outlined' label='Ended' />;
};

const EventsList = ({ events, onAdd, onEdit, onDuplicate, onDelete }: Props) => {
  const [query, setQuery] = useState('');
  const [showPast, setShowPast] = useState(false);

  const overlapping = useMemo(() => getOverlappingIndexes(events), [events]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events
      .map((event, index) => ({ event, index }))
      .filter(({ event }) => !q || `${event.label} ${event.occasion} ${event.start}`.toLowerCase().includes(q));
  }, [events, query]);

  const current = rows.filter(({ event }) => getEventStatus(event) !== 'past');
  const past = rows.filter(({ event }) => getEventStatus(event) === 'past');

  const renderRow = ({ event, index }: { event: BannerEvent; index: number }) => {
    const isPast = getEventStatus(event) === 'past';
    const days = eventDurationDays(event);
    return (
      <Box
        key={`${event.start}-${index}`}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          opacity: isPast ? 0.6 : 1,
        }}>
        <Box sx={{ width: 190, flexShrink: 0 }}>
          <Typography variant='body2' fontWeight={600}>
            {formatDateRange(event.start, event.end)}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {days} {days === 1 ? 'day' : 'days'}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant='body2'>
            {event.emoji} {event.label}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Button: {event.cta}
            {event.occasion ? ` · ${event.occasion}` : ''}
          </Typography>
        </Box>
        <Stack direction='row' spacing={0.5} alignItems='center'>
          {statusChip(event)}
          {overlapping.has(index) && !isPast ? (
            <Tooltip title='Shares days with another event. The shortest event wins.'>
              <Chip size='small' color='warning' label='Overlaps' />
            </Tooltip>
          ) : null}
        </Stack>
        <Stack direction='row' spacing={0.5} sx={{ ml: 'auto' }}>
          <Button size='small' onClick={() => onEdit(index)} aria-label={`Edit ${event.label}`}>
            Edit
          </Button>
          <Button
            size='small'
            color='inherit'
            onClick={() => onDuplicate(index)}
            aria-label={`Duplicate ${event.label}`}>
            Duplicate
          </Button>
          <Button size='small' color='error' onClick={() => onDelete(index)} aria-label={`Delete ${event.label}`}>
            Delete
          </Button>
        </Stack>
      </Box>
    );
  };

  return (
    <Paper variant='outlined'>
      <Stack direction='row' alignItems='center' spacing={2} sx={{ p: 2 }} flexWrap='wrap'>
        <Box flex={1} minWidth={200}>
          <Typography variant='h6' component='h2'>
            Events
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {events.length} total · {current.length} upcoming or live
          </Typography>
        </Box>
        <TextField
          size='small'
          placeholder='Search events'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ ['aria-label']: 'Search events' }}
        />
        <Button variant='contained' onClick={onAdd}>
          Add event
        </Button>
      </Stack>

      {events.length === 0 ? (
        <Typography color='text.secondary' sx={{ p: 2, pt: 0 }}>
          No events yet. The weekly and default banners are used.
        </Typography>
      ) : (
        <>
          {current.length === 0 ? (
            <Typography color='text.secondary' sx={{ px: 2, pb: 2 }}>
              {query ? 'No matching events.' : 'No upcoming events. Add one to schedule a banner.'}
            </Typography>
          ) : (
            current.map(renderRow)
          )}
          {past.length > 0 ? (
            <>
              <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 1 }}>
                <Button size='small' color='inherit' onClick={() => setShowPast((v) => !v)}>
                  {showPast ? 'Hide' : 'Show'} ended events ({past.length})
                </Button>
              </Box>
              {showPast ? past.map(renderRow) : null}
            </>
          ) : null}
        </>
      )}
    </Paper>
  );
};

export default EventsList;
